import { useEffect, useState } from 'react';
import * as S from './Table.styled';
import { Button, Flex, Popconfirm, Table as TableAnt, Tag, Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { sortBy } from 'lodash';
import { TablePaginationConfig } from 'antd/lib/table/interface';
import {
	IAmenity,
	IComplex,
	IComplexCover,
	IComplexPhoto,
	ILot,
	IRegion,
	ILocation,
	IOrganization,
	IPart,
} from 'interfaces/main';
import type { FilterValue, SortOrder, SorterResult } from 'antd/es/table/interface';
import { JSX } from 'react/jsx-runtime';
import { OrganizationsListItemsSelector } from 'features/OrganizationsList/OrganizationsListSlice';
import { useAppSelector } from 'hooks/useStore';
import { LibrariesSelector } from 'features/Libraries/LibrariesSlice';

interface TableParams {
	pagination?: TablePaginationConfig;
	sortField?: string;
	sortOrder?: SortOrder;
	filters?: Record<string, FilterValue>;
}

export interface Props {
	items: IComplex[];
	isDisabled: boolean;
	onEdit(lotId: number): void;
	onRemove(lotId: number): void;
}

const { Text } = Typography;

const Table = ({ items, isDisabled, onEdit, onRemove }: Props): JSX.Element => {
	// Table params
	const [tableParams, setTableParams] = useState<TableParams>({
		pagination: {
			current: 1,
			pageSize: 10,
			total: 0,
		},
		sortField: 'title',
		sortOrder: 'descend',
	});

	// Словари
	const { regions: allRegions } = useAppSelector(LibrariesSelector);

	useEffect(
		() =>
			setTableParams({
				...tableParams,
				pagination: {
					...tableParams.pagination,
					total: items.length,
				},
			}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[items],
	);

	// Доступные организации
	const allOrganizations = useAppSelector(OrganizationsListItemsSelector);

	// const organizations = uniqBy(items.map((item: ILot) => item.)).map(
	//   (email: string) => ({ text: email, value: email })
	// );

	// Конфиг таблички для Ant Table
	const columns: ColumnsType<IComplex> = [
		{
			title: 'Title',
			dataIndex: 'title',
			key: 'title',
			width: 200,
			sorter: (a: IComplex, b: IComplex) => {
				if (a.title < b.title) {
					return -1;
				}
				if (a.title > b.title) {
					return 1;
				}
				return 0;
			},
			defaultSortOrder: 'ascend',
			ellipsis: true,
			fixed: 'left',
			render: (title: string, complex: IComplex) => (
				<Flex vertical>
					<Text ellipsis={true}>{title}</Text>
					<Text type="secondary">{`id: ${complex.id}`}</Text>
				</Flex>
			),
		},
		{
			title: 'Organization',
			dataIndex: 'organizationId',
			key: 'organizationId',
			ellipsis: true,
			width: 170,
			render: (organizationId: number) =>
				allOrganizations?.find((item: IOrganization) => item.id === organizationId)?.title || '-',
		},
		{
			title: 'Region',
			dataIndex: 'regionId',
			key: 'regionId',
			width: 140,
			filters: allRegions.map((item: IRegion) => ({
				text: item.name_en,
				value: item.id,
			})),
			onFilter: (value: number, complex: IComplex) => complex.regionId === value,
			render: (regionId: number) =>
				regionId !== null ? (
					<Tag
						bordered={false}
						color={allRegions.find((p: IRegion) => p.id === regionId).antColor}>
						{allRegions.find((p: IRegion) => p.id === regionId).name_en}
					</Tag>
				) : (
					'-'
				),
		},
		{
			title: 'Year',
			dataIndex: 'year',
			key: 'year',
			width: 80,
			render: (year: number) => year || '-',
		},
		{
			title: 'Quart',
			dataIndex: 'quart',
			key: 'quart',
			width: 80,
			render: (quart: number) => quart || '-',
		},
		{
			title: 'Rating',
			dataIndex: 'rating',
			key: 'quart',
			width: 80,
			render: (rating: number) => rating || '-',
		},
		{
			title: 'Lots',
			dataIndex: 'lots',
			key: 'lots',
			width: 90,
			render: (lots: ILot[] = []) => {
				if (!lots.length) {
					return (
						<Tag color="red" bordered={false}>
							absent
						</Tag>
					);
				}

				const availableLotsCount = lots.filter((lot: ILot) => !lot.isArchived).length;

				return `${availableLotsCount}/${lots.length}`;
			},
		},
		{
			title: 'Photos',
			dataIndex: 'complexPhotos',
			key: 'complexPhotos',
			width: 80,
			render: (photos: IComplexPhoto[]) => photos.length,
		},
		{
			title: 'Cover',
			dataIndex: 'complexCover',
			key: 'complexCover',
			width: 80,
			render: (complexCover: IComplexCover) =>
				!!complexCover ? (
					<Tag bordered={false} color="blue">
						yes
					</Tag>
				) : (
					<Tag bordered={false} color="default">
						no
					</Tag>
				),
		},
		{
			title: 'Amenities',
			dataIndex: 'amenities',
			key: 'amenities',
			width: 300,
			render: (amenities: IAmenity[]) =>
				amenities.length ? (
					<S.Tags>
						{amenities.map((item: IAmenity) => (
							<Tag key={item.id} bordered={false}>
								{item.name_en}
							</Tag>
						))}
					</S.Tags>
				) : (
					<Tag color="red" bordered={false}>
						absent
					</Tag>
				),
		},
		{
			title: 'Locations',
			dataIndex: 'locations',
			key: 'locations',
			width: 300,
			render: (locations: ILocation[]) =>
				locations.length ? (
					<S.Tags>
						{sortBy(locations, 'id').map((item: ILocation) => (
							<Tag bordered={false} key={item.id}>
								{item.name}
							</Tag>
						))}
					</S.Tags>
				) : (
					<Tag bordered={false} color="red">
						absent
					</Tag>
				),
		},
		{
			title: 'Payment',
			dataIndex: 'parts',
			key: 'parts',
			width: 300,
			render: (parts: IPart[]) =>
				parts.length ? (
					<S.Tags>
						{sortBy(parts, 'order').map((item: IPart) => (
							<Tag key={item.id} bordered={false}>
								{`${item.order}. ${item.title_en} - ${item.value}%`}
							</Tag>
						))}
					</S.Tags>
				) : (
					<Tag color="red" bordered={false}>
						absent
					</Tag>
				),
		},
		{
			title: '',
			key: 'action',
			fixed: 'right',
			width: 150,
			render: (_: string, complex: IComplex) => {
				return (
					<S.Actions>
						<Button onClick={() => onEdit(complex.id)} size="small" disabled={isDisabled}>
							edit
						</Button>
						<Popconfirm
							placement="rightBottom"
							title="Are you sure to delete this complex?"
							onConfirm={() => onRemove(complex.id)}
							okText="delete"
							cancelText="cancel"
							disabled={isDisabled}>
							<Button size="small" disabled={isDisabled}>
								delete
							</Button>
						</Popconfirm>
					</S.Actions>
				);
			},
		},
	];

	const handleTableChange = (
		pagination: TablePaginationConfig,
		filters: Record<string, FilterValue>,
		sorter: SorterResult<IComplex>,
	) => {
		setTableParams({
			pagination,
			filters,
			...sorter,
		});
	};

	return (
		<S.Wrap>
			<TableAnt
				columns={columns}
				rowKey={(complex: IComplex) => complex.id}
				dataSource={items}
				scroll={{ x: '1400' }}
				pagination={false}
				onChange={handleTableChange}
			/>
		</S.Wrap>
	);
};

export default Table;
